import React from 'react';
import { IoReceiptOutline, IoTimeOutline, IoCloseCircleOutline, IoReload } from 'react-icons/io5';
//
import Botao from '../../components/Botao';
//
import styles from './fatura-fallback.module.css';

export type FaturaFallbackReason = 'not_found' | 'expired' | 'invalid' | 'network' | 'unknown';

interface FaturaFallbackProps {
  /** Tipo do problema — define ícone, título e mensagem */
  reason?: FaturaFallbackReason;
  /** Detalhes técnicos (status HTTP, mensagem do backend, etc) */
  detail?: string;
  /** Callback de retry. Se ausente, recarrega a página */
  onRetry?: () => void;
}

interface PresetCopy {
  title: string;
  message: string;
  icon: React.ReactNode;
  iconBg: string;
  iconBorder: string;
  iconColor: string;
}

const COPY: Record<FaturaFallbackReason, PresetCopy> = {
  not_found: {
    title: 'Fatura não encontrada',
    message:
      'O link que você acessou não corresponde a uma cobrança válida. Verifique se o endereço está correto ou solicite um novo link à operadora.',
    icon: <IoReceiptOutline />,
    iconBg: 'var(--color-info-bg)',
    iconBorder: 'var(--color-info-border)',
    iconColor: 'var(--color-info)',
  },
  expired: {
    title: 'Link expirado',
    message:
      'Esta cobrança não está mais disponível para pagamento. Entre em contato com a operadora para receber um novo link.',
    icon: <IoTimeOutline />,
    iconBg: 'var(--color-warning-bg)',
    iconBorder: 'var(--color-warning-border)',
    iconColor: 'var(--color-warning)',
  },
  invalid: {
    title: 'Link inválido',
    message:
      'Os dados desta cobrança estão incompletos. Tente abrir o link novamente ou solicite outro à operadora.',
    icon: <IoCloseCircleOutline />,
    iconBg: 'var(--color-error-bg)',
    iconBorder: 'var(--color-error-border)',
    iconColor: 'var(--color-error)',
  },
  network: {
    title: 'Falha de conexão',
    message:
      'Não foi possível carregar sua fatura. Verifique sua conexão com a internet e tente novamente.',
    icon: <IoCloseCircleOutline />,
    iconBg: 'var(--color-error-bg)',
    iconBorder: 'var(--color-error-border)',
    iconColor: 'var(--color-error)',
  },
  unknown: {
    title: 'Não foi possível abrir a fatura',
    message: 'Encontramos um problema ao carregar esta cobrança. Tente novamente em instantes.',
    icon: <IoCloseCircleOutline />,
    iconBg: 'var(--color-error-bg)',
    iconBorder: 'var(--color-error-border)',
    iconColor: 'var(--color-error)',
  },
};

export default function FaturaFallback({ reason = 'unknown', detail, onRetry }: FaturaFallbackProps) {
  const copy = COPY[reason];

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  return (
    <div className={styles['container']}>
      <div className={styles['card']}>
        <div
          className={styles['icon-wrapper']}
          style={{ background: copy.iconBg, borderColor: copy.iconBorder, color: copy.iconColor }}
        >
          {copy.icon}
        </div>

        <h1 className={styles['title']}>{copy.title}</h1>
        <p className={styles['message']}>{copy.message}</p>

        {detail && (
          <details className={styles['details']}>
            <summary>Detalhes técnicos</summary>
            <code className={styles['detail-text']}>{detail}</code>
          </details>
        )}

        <Botao
          icon={<IoReload />}
          text="Tentar novamente"
          onClick={handleRetry}
          color="primary"
          style={{ marginTop: '1rem' }}
        />
      </div>
    </div>
  );
}
